import { Injectable } from '@angular/core';

import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import type { Icons } from '../component/snack-bar/snack-bar.component';
import { SnackBarComponent } from '../component/snack-bar/snack-bar.component';

const ACTION_MESSAGE = {
  close: 'SNACK_BAR.ACTIONS.CLOSE',
  undo: 'SNACK_BAR.ACTIONS.UNDO',
  update: 'SNACK_BAR.ACTIONS.UPDATE',
} as const;

@Injectable({
  providedIn: 'root',
})
export class SnackBarService {
  private _config: MatSnackBarConfig = {
    verticalPosition: 'bottom',
    horizontalPosition: 'right',
    duration: 10000,
  };

  constructor(
    private _snackBar: MatSnackBar,
    private _translate: TranslateService
  ) {}

  private _open(args: { callbackAction: Function; type: keyof Icons; config: MatSnackBarConfig }) {
    const bar = this._snackBar.openFromComponent(SnackBarComponent, args.config);

    bar.afterDismissed().subscribe(({ dismissedByAction }) => {
      // If snackbar is an update type, we want to keep it active (like update notification)
      if (args.type === 'update' && !dismissedByAction) {
        setTimeout(() => this._open(args), this._config.duration);
      }
    });

    bar.onAction().subscribe(() => args.callbackAction());
  }

  /**
   * Send a snackbar one at a time
   * @param content - Specify message (with translation key or not), type, and optionally a value for translation
   * @param inputAction - Optional action on snackbar
   */
  send(
    content: { message: string; type: keyof Icons; value?: string | number },
    inputAction: {
      type?: keyof typeof ACTION_MESSAGE;
      callback?: () => void;
    } = {}
  ) {
    const action = {
      type: 'close',
      callback: () => {},
      ...inputAction,
    };

    this._translate
      .get(content.message, { value: content.value })
      .pipe(take(1))
      .subscribe((translatedMessage: string) => {
        this._open({
          callbackAction: action.callback,
          type: content.type,
          config: {
            ...this._config,
            duration: content.type === 'update' ? undefined : this._config.duration,
            data: {
              message: translatedMessage,
              action: ACTION_MESSAGE[action.type],
              type: content.type,
            },
          },
        });
      });
  }
}
